import { Alert, Button, Grid, Space, Table, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { useAuth } from "../app/auth.tsx";
import { formatDate } from "../app/classes.ts";
import { formatSchoolDateTime } from "../app/time.ts";
import { teacherMessageKeys, type TeacherMessage } from "../app/teacherMessages.ts";
import { TeacherMessageComposerDrawer } from "../components/TeacherMessageComposerDrawer.tsx";
import { TeacherMessageLogFilters, type TeacherMessageLogFilterValues } from "../components/TeacherMessageLogFilters.tsx";

const PAGE_SIZE = 20;

/**
 * Teacher-to-parent messages (bd-3qg.12): teachers (teacherMessages.send) write to the parents of a class they teach
 * today; the director (teacherMessages.read) reads the log of every message with its recipients and delivery status,
 * filtered by teacher and class (bd-3qg.22).
 */
export function TeacherMessagesPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const screens = Grid.useBreakpoint();
  const canSend = hasPermission("teacherMessages.send");
  const canRead = hasPermission("teacherMessages.read");
  const [composing, setComposing] = useState(false);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<TeacherMessageLogFilterValues>({ teacherId: undefined, classId: undefined });
  const filtered = filters.teacherId !== undefined || filters.classId !== undefined;

  const query = useQuery({
    queryKey: teacherMessageKeys.list({ page, ...filters }),
    enabled: canRead,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = {
        ...(filters.teacherId ? { TeacherId: filters.teacherId } : {}),
        ...(filters.classId ? { ClassId: filters.classId } : {}),
        Page: page,
        PageSize: PAGE_SIZE,
      };
      const { data, error } = await api.GET("/teacher-messages", { params: { query: params } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });

  const columns = [
    {
      key: "message",
      title: t("teacherMessages.columns.message"),
      render: (_: unknown, m: TeacherMessage) => (
        <Space orientation="vertical" size={2} style={{ width: "100%" }}>
          <Typography.Paragraph style={{ margin: 0, whiteSpace: "pre-wrap" }} ellipsis={{ rows: 3, expandable: true }}>
            {m.text}
          </Typography.Paragraph>
          <Typography.Text type="secondary">{t("teacherMessages.by", { name: m.teacherName, time: formatSchoolDateTime(m.sentAt) })}</Typography.Text>
        </Space>
      ),
    },
    { key: "audience", title: t("teacherMessages.columns.audience"), width: 260, render: (_: unknown, m: TeacherMessage) => <AudienceCell message={m} /> },
    { key: "delivery", title: t("teacherMessages.columns.delivery"), width: 240, render: (_: unknown, m: TeacherMessage) => <DeliveryCell message={m} /> },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {t("teacherMessages.title")}
          </Typography.Title>
          <Typography.Text type="secondary">{canRead ? t("teacherMessages.subtitle") : t("teacherMessages.subtitleTeacher")}</Typography.Text>
        </div>
        {canSend && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setComposing(true)}>
            {t("teacherMessages.new")}
          </Button>
        )}
      </div>
      {!canRead && <Alert type="info" showIcon title={t("teacherMessages.logHidden")} />}
      {canRead && (
        <TeacherMessageLogFilters
          value={filters}
          onChange={(next) => {
            setFilters(next);
            setPage(1);
          }}
        />
      )}
      {canRead && query.isError && (
        <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void query.refetch()}>{t("app.retry")}</Button>} />
      )}
      {canRead && (
        <Table<TeacherMessage>
          rowKey="id"
          size={screens.md ? "middle" : "small"}
          loading={query.isPending || (query.isFetching && query.isPlaceholderData)}
          columns={columns}
          dataSource={query.data?.items ?? []}
          locale={{ emptyText: filtered ? t("teacherMessages.emptyFiltered") : t("teacherMessages.empty") }}
          scroll={{ x: 900 }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: Number(query.data?.totalCount ?? 0),
            showSizeChanger: false,
            hideOnSinglePage: true,
            showTotal: (total: number) => t("teacherMessages.total", { count: total }),
            onChange: setPage,
          }}
        />
      )}
      {canSend && <TeacherMessageComposerDrawer open={composing} onClose={() => setComposing(false)} />}
    </Space>
  );
}

function AudienceCell({ message: m }: { message: TeacherMessage }) {
  const { t } = useTranslation();
  return (
    <Space orientation="vertical" size={0}>
      <Typography.Text>
        {m.audience === "Student" && m.studentName
          ? t("teacherMessages.audienceStudent", { student: m.studentName, className: m.className })
          : t("teacherMessages.audienceClass", { className: m.className })}
      </Typography.Text>
      <Typography.Text type="secondary">
        {m.recipientNames.length > 0 ? t("teacherMessages.recipients", { names: m.recipientNames.join(", ") }) : t("teacherMessages.noRecipients")}
      </Typography.Text>
    </Space>
  );
}

function DeliveryCell({ message: m }: { message: TeacherMessage }) {
  const { t } = useTranslation();
  const d = m.delivery;
  return (
    <Space orientation="vertical" size={0}>
      <Typography.Text>{t("teacherMessages.queued", { count: Number(m.queuedCount), date: formatDate(m.recipientDate) })}</Typography.Text>
      <Typography.Text type="secondary">
        {t("teacherMessages.counts", {
          sent: Number(d.sent),
          pending: Number(d.pending),
          failed: Number(d.failed),
          unreachable: Number(d.unreachable),
          skipped: Number(d.skipped),
        })}
      </Typography.Text>
      <Typography.Text type={Number(m.notLinkedCount) > 0 ? "warning" : "secondary"}>{t("teacherMessages.notLinked", { count: Number(m.notLinkedCount) })}</Typography.Text>
    </Space>
  );
}
